/**
 * Default Momentum Rules — Platform-level signal producers
 *
 * These fire for ALL tenants. Tenant config can disable specific rules
 * via `disabledRules` or adjust thresholds to control severity.
 *
 * Rule conditions use simple JS expressions evaluated against a `ctx` object:
 *   ctx.entity      — current entity data (job, trip, invoice, etc.)
 *   ctx.list        — list-level aggregate data (counts, totals)
 *   ctx.thresholds  — tenant thresholds from MomentumConfig
 *   ctx.pageKey     — current page key
 *   ctx.now         — current timestamp (ms)
 */

import type { MomentumRule } from './types';

export const DEFAULT_RULES: MomentumRule[] = [
  // ══════════════════════════════════════════════════════════════════════════
  // REVENUE DIMENSION
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'rev.job_completed_no_invoice',
    pagePattern: '*.jobs*',
    signalType: 'revenue_blocked',
    defaultSeverity: 'warning',
    messageTemplate: '{{entityLabel}}: completed {{hoursAgo}}h ago, no invoice yet — ${{entity.estimatedRevenue}} blocked',
    messageTemplateZh: '{{entityLabel}}: 已完成 {{hoursAgo}} 小时，未出账单 — ${{entity.estimatedRevenue}} 收入被阻塞',
    monetaryExpression: 'entity.estimatedRevenue',
    timeExpression: 'hoursAgo',
    condition: "entity.status === 'completed' && !entity.invoiceId && hoursAgo > thresholds.invoiceDelayWarnHours",
    suggestedAction: {
      labelTemplate: 'Generate invoice for {{entityLabel}}',
      labelTemplateZh: '为 {{entityLabel}} 生成账单',
      prompt: 'Generate invoice for job {{entityLabel}}',
    },
    priority: 10,
  },
  {
    id: 'rev.job_completed_no_invoice_critical',
    pagePattern: '*.jobs*',
    signalType: 'revenue_blocked',
    defaultSeverity: 'critical',
    messageTemplate: '{{entityLabel}}: completed {{hoursAgo}}h ago — ${{entity.estimatedRevenue}} URGENT',
    messageTemplateZh: '{{entityLabel}}: 完成已过 {{hoursAgo}} 小时 — ${{entity.estimatedRevenue}} 紧急',
    monetaryExpression: 'entity.estimatedRevenue',
    condition: "entity.status === 'completed' && !entity.invoiceId && hoursAgo > thresholds.invoiceDelayCriticalHours",
    suggestedAction: {
      labelTemplate: 'Invoice {{entityLabel}} NOW',
      labelTemplateZh: '立即为 {{entityLabel}} 出账单',
      prompt: 'Generate invoice for job {{entityLabel}} immediately',
      requireConfirmation: false,
    },
    priority: 5,
  },
  {
    id: 'rev.missing_pod',
    pagePattern: '*.jobs*',
    signalType: 'revenue_blocked',
    defaultSeverity: 'warning',
    messageTemplate: '{{entityLabel}}: POD not uploaded — cannot invoice ${{entity.estimatedRevenue}}',
    messageTemplateZh: '{{entityLabel}}: POD 未上传 — 无法开票 ${{entity.estimatedRevenue}}',
    monetaryExpression: 'entity.estimatedRevenue',
    condition: "entity.status === 'completed' && !entity.podUploaded",
    suggestedAction: {
      labelTemplate: 'Upload POD for {{entityLabel}}',
      labelTemplateZh: '上传 {{entityLabel}} 的 POD',
      route: '/{{entity.module}}/jobs/{{entity.id}}?tab=docs',
    },
    priority: 15,
  },
  {
    id: 'rev.missing_critical_data',
    pagePattern: '*.jobs*',
    signalType: 'revenue_blocked',
    defaultSeverity: 'warning',
    messageTemplate: '{{entityLabel}}: missing {{missingFields}} — cannot dispatch',
    messageTemplateZh: '{{entityLabel}}: 缺少 {{missingFields}} — 无法派车',
    monetaryExpression: 'entity.estimatedRevenue',
    condition: 'entity.missingCriticalFields && entity.missingCriticalFields.length > 0',
    suggestedAction: {
      labelTemplate: 'Complete {{entityLabel}} data',
      labelTemplateZh: '补全 {{entityLabel}} 数据',
      route: '/{{entity.module}}/jobs/{{entity.id}}?tab=details',
    },
    priority: 20,
  },
  {
    id: 'rev.ready_to_bill',
    pagePattern: '*.jobs*',
    signalType: 'revenue_ready',
    defaultSeverity: 'positive',
    messageTemplate: '{{entityLabel}}: ready to invoice — ${{entity.estimatedRevenue}}',
    messageTemplateZh: '{{entityLabel}}: 可出账单 — ${{entity.estimatedRevenue}}',
    monetaryExpression: 'entity.estimatedRevenue',
    condition: "entity.status === 'completed' && entity.podUploaded && !entity.invoiceId",
    suggestedAction: {
      labelTemplate: 'Invoice now',
      labelTemplateZh: '立即出账单',
      prompt: 'Generate invoice for job {{entityLabel}}',
    },
    priority: 50,
  },

  // ══════════════════════════════════════════════════════════════════════════
  // SERVICE DIMENSION
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'svc.sla_approaching',
    pagePattern: '*.jobs*,*.dispatch*,*.trips*',
    signalType: 'service_risk',
    defaultSeverity: 'critical',
    messageTemplate: '{{entityLabel}}: SLA in {{hoursRemaining}}h — {{entity.customerName}}',
    messageTemplateZh: '{{entityLabel}}: SLA 还剩 {{hoursRemaining}} 小时 — {{entity.customerName}}',
    timeExpression: 'hoursRemaining',
    condition: 'hoursRemaining > 0 && hoursRemaining < thresholds.slaRiskWindowHours',
    suggestedAction: {
      labelTemplate: 'Assign driver or notify customer',
      labelTemplateZh: '分配司机或通知客户',
      prompt: 'Job {{entityLabel}} SLA approaching. Assign a driver or notify the customer.',
    },
    priority: 3,
  },
  {
    id: 'svc.trip_unassigned',
    pagePattern: '*.jobs*,*.dispatch*,*.trips*',
    signalType: 'service_risk',
    defaultSeverity: 'warning',
    messageTemplate: '{{entityLabel}}: trip not assigned, due in {{hoursRemaining}}h',
    messageTemplateZh: '{{entityLabel}}: 行程未分配司机，{{hoursRemaining}} 小时后到期',
    timeExpression: 'hoursRemaining',
    condition: "!entity.driverId && entity.tripStatus !== 'completed' && hoursRemaining < 24",
    suggestedAction: {
      labelTemplate: 'Assign driver',
      labelTemplateZh: '分配司机',
      prompt: 'Optimize dispatch and assign driver for {{entityLabel}}',
    },
    priority: 8,
  },
  {
    id: 'svc.compliance_below_threshold',
    pagePattern: '*.jobs*',
    signalType: 'service_risk',
    defaultSeverity: 'warning',
    messageTemplate: '{{entityLabel}}: compliance {{entity.complianceScore}}% — {{entity.complianceGaps}} gaps',
    messageTemplateZh: '{{entityLabel}}: 合规 {{entity.complianceScore}}% — {{entity.complianceGaps}} 项不达标',
    condition: 'entity.complianceScore != null && entity.complianceScore < 80',
    suggestedAction: {
      labelTemplate: 'View compliance details',
      labelTemplateZh: '查看合规详情',
      route: '/{{entity.module}}/jobs/{{entity.id}}?tab=compliance',
    },
    priority: 25,
  },

  // ══════════════════════════════════════════════════════════════════════════
  // CASH DIMENSION
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'cash.ar_overdue_warn',
    pagePattern: '*.billing*,*.ar*,finance.*',
    signalType: 'cash_delayed',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.customerName}}: ${{entity.overdueAmount}} overdue {{entity.overdueDays}}d',
    messageTemplateZh: '{{entity.customerName}}: ${{entity.overdueAmount}} 逾期 {{entity.overdueDays}} 天',
    monetaryExpression: 'entity.overdueAmount',
    timeExpression: 'entity.overdueDays',
    condition: 'entity.overdueDays >= thresholds.arOverdueWarnDays && entity.overdueDays < thresholds.arOverdueCriticalDays',
    suggestedAction: {
      labelTemplate: 'Send collection reminder',
      labelTemplateZh: '发送催收提醒',
      prompt: 'Send collection reminder to {{entity.customerName}} for ${{entity.overdueAmount}} overdue {{entity.overdueDays}} days',
    },
    priority: 12,
  },
  {
    id: 'cash.ar_overdue_critical',
    pagePattern: '*.billing*,*.ar*,finance.*',
    signalType: 'cash_delayed',
    defaultSeverity: 'critical',
    messageTemplate: '{{entity.customerName}}: ${{entity.overdueAmount}} overdue {{entity.overdueDays}}d — ESCALATE',
    messageTemplateZh: '{{entity.customerName}}: ${{entity.overdueAmount}} 逾期 {{entity.overdueDays}} 天 — 需升级处理',
    monetaryExpression: 'entity.overdueAmount',
    condition: 'entity.overdueDays >= thresholds.arOverdueCriticalDays',
    suggestedAction: {
      labelTemplate: 'Escalate collection',
      labelTemplateZh: '升级催收',
      prompt: 'Escalate collection for {{entity.customerName}} — ${{entity.overdueAmount}} overdue {{entity.overdueDays}} days',
      requireConfirmation: true,
    },
    priority: 4,
  },

  // ══════════════════════════════════════════════════════════════════════════
  // GROWTH DIMENSION
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'grow.customer_declining',
    pagePattern: '*.dashboard*,*.customers*',
    signalType: 'growth_warning',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.customerName}}: orders down {{entity.declinePct}}% vs last month',
    messageTemplateZh: '{{entity.customerName}}: 本月下单减少 {{entity.declinePct}}%（对比上月）',
    condition: 'entity.declinePct > thresholds.customerDeclinePct',
    suggestedAction: {
      labelTemplate: 'Reach out to {{entity.customerName}}',
      labelTemplateZh: '主动联系 {{entity.customerName}}',
      prompt: 'Customer {{entity.customerName}} order volume is down {{entity.declinePct}}% this month. Draft a check-in message.',
    },
    priority: 30,
  },
  {
    id: 'grow.capacity_near_limit',
    pagePattern: '*.dashboard*,*.dispatch*',
    signalType: 'growth_warning',
    defaultSeverity: 'warning',
    messageTemplate: 'Capacity at {{entity.capacityPct}}% — may need to expand',
    messageTemplateZh: '运力利用率 {{entity.capacityPct}}% — 可能需要扩容',
    condition: 'entity.capacityPct > thresholds.capacityWarnPct',
    suggestedAction: {
      labelTemplate: 'Review capacity plan',
      labelTemplateZh: '查看运力规划',
      route: '/{{entity.module}}/dispatch',
    },
    priority: 35,
  },

  // ══════════════════════════════════════════════════════════════════════════
  // HMS-SPECIFIC RULES (work with actual HmsJob fields)
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'hms.lfd_overdue',
    pagePattern: 'hms.*',
    signalType: 'service_risk',
    defaultSeverity: 'critical',
    messageTemplate: '{{entity.jobNumber}}: LFD overdue — demurrage accumulating',
    messageTemplateZh: '{{entity.jobNumber}}: 超过最后免费期限 — 滞柜费在累加',
    condition: "entity.lfd && entity.lfd.length >= 10 && entity.status !== 'completed' && entity.status !== 'cancelled' && new Date(entity.lfd).getTime() < now",
    suggestedAction: {
      labelTemplate: 'Prioritize {{entity.jobNumber}}',
      labelTemplateZh: '优先处理 {{entity.jobNumber}}',
      route: '/hms/jobs/{{entity.id}}',
    },
    priority: 1,
  },
  {
    id: 'hms.lfd_approaching',
    pagePattern: 'hms.*',
    signalType: 'service_risk',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.jobNumber}}: LFD in less than 2 days',
    messageTemplateZh: '{{entity.jobNumber}}: 最后免费期限不到2天',
    condition: "entity.lfd && entity.lfd.length >= 10 && entity.status !== 'completed' && entity.status !== 'cancelled' && (new Date(entity.lfd).getTime() - now) < 172800000 && (new Date(entity.lfd).getTime() - now) > 0",
    suggestedAction: {
      labelTemplate: 'Plan collection for {{entity.jobNumber}}',
      labelTemplateZh: '安排 {{entity.jobNumber}} 的提柜',
      route: '/hms/jobs/{{entity.id}}',
    },
    priority: 5,
  },
  {
    id: 'hms.no_driver_assigned',
    pagePattern: 'hms.*',
    signalType: 'service_risk',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.jobNumber}}: no driver assigned — {{entity.jobType}} {{entity.containerNo}}',
    messageTemplateZh: '{{entity.jobNumber}}: 未分配司机 — {{entity.jobType}} {{entity.containerNo}}',
    condition: "(!entity.driverName || entity.driverName === '—' || entity.driverName === '') && entity.status !== 'draft' && entity.status !== 'completed' && entity.status !== 'cancelled'",
    suggestedAction: {
      labelTemplate: 'Assign driver',
      labelTemplateZh: '分配司机',
      prompt: 'Assign a driver for job {{entity.jobNumber}} ({{entity.jobType}} {{entity.containerNo}})',
    },
    priority: 8,
  },
  {
    id: 'hms.no_container_no',
    pagePattern: 'hms.*',
    signalType: 'revenue_blocked',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.jobNumber}}: missing container number — cannot dispatch',
    messageTemplateZh: '{{entity.jobNumber}}: 缺少柜号 — 无法派车',
    condition: "(!entity.containerNo || entity.containerNo === '') && entity.status !== 'cancelled'",
    suggestedAction: {
      labelTemplate: 'Complete job data',
      labelTemplateZh: '补全任务数据',
      route: '/hms/jobs/{{entity.id}}',
    },
    priority: 15,
  },
  {
    id: 'hms.urgent_flag',
    pagePattern: 'hms.*',
    signalType: 'service_risk',
    defaultSeverity: 'critical',
    messageTemplate: '{{entity.jobNumber}}: URGENT — {{entity.jobType}} {{entity.containerNo}} {{entity.customerName}}',
    messageTemplateZh: '{{entity.jobNumber}}: 加急 — {{entity.jobType}} {{entity.containerNo}} {{entity.customerName}}',
    condition: "entity.urgent === true && entity.status !== 'completed' && entity.status !== 'cancelled'",
    suggestedAction: {
      labelTemplate: 'Handle urgent job',
      labelTemplateZh: '处理加急任务',
      route: '/hms/jobs/{{entity.id}}',
    },
    priority: 2,
  },
  {
    id: 'hms.delivered_ok',
    pagePattern: 'hms.*',
    signalType: 'service_delivered',
    defaultSeverity: 'positive',
    messageTemplate: '{{entity.jobNumber}}: delivered ✓ — {{entity.containerNo}}',
    messageTemplateZh: '{{entity.jobNumber}}: 已送达 ✓ — {{entity.containerNo}}',
    condition: "entity.status === 'delivered' || entity.status === 'completed'",
    priority: 60,
  },

  // ══════════════════════════════════════════════════════════════════════════
  // TMS-SPECIFIC RULES (work with TruckingJob fields: container_no, driver_name, lfd)
  // ══════════════════════════════════════════════════════════════════════════

  {
    id: 'tms.lfd_overdue',
    pagePattern: 'tms.*',
    signalType: 'service_risk',
    defaultSeverity: 'critical',
    messageTemplate: '{{entity.jobNumber}}: LFD overdue — demurrage accumulating',
    messageTemplateZh: '{{entity.jobNumber}}: 超过最后免费期限 — 滞柜费在累加',
    condition: "entity.lfd && entity.lfd.length >= 10 && entity.status !== 'completed' && entity.status !== 'cancelled' && new Date(entity.lfd).getTime() < now",
    suggestedAction: {
      labelTemplate: 'Prioritize {{entity.jobNumber}}',
      labelTemplateZh: '优先处理 {{entity.jobNumber}}',
      route: '/tms/jobs/{{entity.id}}',
    },
    priority: 1,
  },
  {
    id: 'tms.lfd_approaching',
    pagePattern: 'tms.*',
    signalType: 'service_risk',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.jobNumber}}: LFD in less than 2 days',
    messageTemplateZh: '{{entity.jobNumber}}: 最后免费期限不到2天',
    condition: "entity.lfd && entity.lfd.length >= 10 && entity.status !== 'completed' && entity.status !== 'cancelled' && (new Date(entity.lfd).getTime() - now) < 172800000 && (new Date(entity.lfd).getTime() - now) > 0",
    suggestedAction: {
      labelTemplate: 'Plan collection for {{entity.jobNumber}}',
      labelTemplateZh: '安排 {{entity.jobNumber}} 的提柜',
      route: '/tms/jobs/{{entity.id}}',
    },
    priority: 5,
  },
  {
    id: 'tms.no_driver_assigned',
    pagePattern: 'tms.*',
    signalType: 'service_risk',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.jobNumber}}: no driver assigned — {{entity.type}} {{entity.container_no}}',
    messageTemplateZh: '{{entity.jobNumber}}: 未分配司机 — {{entity.type}} {{entity.container_no}}',
    condition: "(!entity.driver_name || entity.driver_name === '') && entity.status !== 'draft' && entity.status !== 'completed' && entity.status !== 'cancelled'",
    suggestedAction: {
      labelTemplate: 'Assign driver',
      labelTemplateZh: '分配司机',
      prompt: 'Assign a driver for TMS job {{entity.jobNumber}} ({{entity.type}} {{entity.container_no}})',
    },
    priority: 8,
  },
  {
    id: 'tms.no_container_no',
    pagePattern: 'tms.*',
    signalType: 'revenue_blocked',
    defaultSeverity: 'warning',
    messageTemplate: '{{entity.jobNumber}}: missing container number — cannot dispatch',
    messageTemplateZh: '{{entity.jobNumber}}: 缺少柜号 — 无法派车',
    condition: "(!entity.container_no || entity.container_no === '') && entity.status !== 'cancelled'",
    suggestedAction: {
      labelTemplate: 'Complete job data',
      labelTemplateZh: '补全任务数据',
      route: '/tms/jobs/{{entity.id}}',
    },
    priority: 15,
  },
  {
    id: 'tms.urgent_flag',
    pagePattern: 'tms.*',
    signalType: 'service_risk',
    defaultSeverity: 'critical',
    messageTemplate: '{{entity.jobNumber}}: URGENT — {{entity.type}} {{entity.container_no}} {{entity.customer}}',
    messageTemplateZh: '{{entity.jobNumber}}: 加急 — {{entity.type}} {{entity.container_no}} {{entity.customer}}',
    condition: "entity.urgent === true && entity.status !== 'completed' && entity.status !== 'cancelled'",
    suggestedAction: {
      labelTemplate: 'Handle urgent job',
      labelTemplateZh: '处理加急任务',
      route: '/tms/jobs/{{entity.id}}',
    },
    priority: 2,
  },
  {
    id: 'tms.delivered_ok',
    pagePattern: 'tms.*',
    signalType: 'service_delivered',
    defaultSeverity: 'positive',
    messageTemplate: '{{entity.jobNumber}}: delivered ✓ — {{entity.container_no}}',
    messageTemplateZh: '{{entity.jobNumber}}: 已送达 ✓ — {{entity.container_no}}',
    condition: "entity.status === 'delivered' || entity.status === 'completed'",
    priority: 60,
  },
];
